import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import EmailComposer from "../components/profile-components/EmailComposer";
import AddressVerificationFields from "../components/common/AddressVerificationFields";
import ActivityLogPanel from "../components/profile-components/ActivityLogPanel";
import API_BASE_URL from "../config";
import "./SalesPages.css";
import usePageTitle from "../hooks/usePageTitle";

function SalesLeadProfile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [salesLead, setSalesLead] = useState(null);
  const [owners, setOwners] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditingSubscription, setIsEditingSubscription] = useState(false);
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const salesLeadPageTitle =
    salesLead?.agency_name ||
    `${salesLead?.first_name || ''} ${salesLead?.last_name || ''}`.trim() ||
    'Sales Lead Profile';

  usePageTitle(salesLeadPageTitle);

  useEffect(() => {
    loadSalesLead();
    loadOwners();
    loadPlans();
  }, [id]);

  const loadSalesLead = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/sales-leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error("Failed to load sales lead");

      const data = await res.json();
      setSalesLead(data);
      setIsEditingSubscription(false);
    } catch (err) {
      console.error("Load sales lead error:", err.message);
      setSalesLead(null);
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/users/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const data = await res.json();
      setOwners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load owners error:", err.message);
    }
  };

  const loadPlans = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/subscription-plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) return;

      const data = await res.json();
      setPlans(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Load plans error:", err.message);
    }
  };

  const handleChange = (field, value) => {
    setSalesLead((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddressChange = (fieldName, value) => {
    handleChange(fieldName, value);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/sales-leads/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: salesLead.status,
          source: salesLead.source,
          first_name: salesLead.first_name,
          last_name: salesLead.last_name,
          agency_name: salesLead.agency_name,
          phone: salesLead.phone,
          email: salesLead.email,
          website: salesLead.website,
          business_address: salesLead.business_address,
          city: salesLead.city,
          state: salesLead.state,
          zip_code: salesLead.zip_code,
          subscription_status: salesLead.subscription_status,
          subscription_plan: salesLead.subscription_plan,
          billing_status: salesLead.billing_status,
          owner_user_id: salesLead.owner_user_id || null
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Failed to save sales lead");

      alert("Sales lead saved successfully.");
      setIsEditingSubscription(false);
      loadSalesLead();
    } catch (err) {
      console.error("Save sales lead error:", err.message);
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEmailComposer = (email) => {
    setEmailRecipient(email || salesLead?.email || "");
    setShowEmailComposer(true);
  };

  const handleCloseEmailComposer = () => {
    setShowEmailComposer(false);
    setEmailRecipient("");
  };

  const fullName =
    `${salesLead?.first_name || ""} ${salesLead?.last_name || ""}`.trim() || "Sales Lead";

  const selectedPlan = useMemo(() => {
    return plans.find((plan) => String(plan.plan_key) === String(salesLead?.subscription_plan));
  }, [plans, salesLead?.subscription_plan]);

  const mergeData = {
    first_name: salesLead?.first_name || "",
    last_name: salesLead?.last_name || "",
    full_name: fullName,
    agency_name: salesLead?.agency_name || "",
    email: salesLead?.email || ""
  };

  const statusClass = (value) =>
    `sales-chip ${String(value || "").toLowerCase().replace(/\s+/g, "-")}`;

  if (loading) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Loading sales lead...</div>
        </div>
      </>
    );
  }

  if (!salesLead) {
    return (
      <>
        <Navbar />
        <Topbar />
        <div className="main-content sales-page">
          <div className="sales-section-card">Sales lead not found.</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">{fullName}</h1>
            <p className="sales-subtitle">
              Manage sales lead details, agency information, ownership, subscription interest, and communications.
            </p>

            <div className="sales-hero-meta">
              <span className={statusClass(salesLead.status || "unknown")}>
                {salesLead.status || "Unknown"}
              </span>

              {salesLead.source && (
                <span className="sales-chip sales-chip-neutral">{salesLead.source}</span>
              )}

              {salesLead.agency_name && (
                <span className="sales-chip sales-chip-outline">{salesLead.agency_name}</span>
              )}

              {salesLead.subscription_status && (
                <span className={statusClass(salesLead.subscription_status)}>
                  {salesLead.subscription_status}
                </span>
              )}

              {salesLead.billing_status && (
                <span className={statusClass(salesLead.billing_status)}>
                  {salesLead.billing_status}
                </span>
              )}

              {(selectedPlan?.plan_name || salesLead.subscription_plan) && (
                <span className="sales-chip sales-chip-outline">
                  {selectedPlan?.plan_name || salesLead.subscription_plan}
                </span>
              )}
            </div>
          </div>

          <div className="sales-header-actions">
            <button className="sales-secondary-btn" onClick={() => navigate("/sales-leads")}>
              Back to Sales Leads
            </button>

            <button
              className="sales-secondary-btn"
              onClick={() => handleOpenEmailComposer(salesLead.email)}
            >
              Send Email
            </button>

            <button className="sales-primary-btn" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {showEmailComposer ? (
          <EmailComposer
            recipientEmail={emailRecipient || salesLead.email || ""}
            recipientName={fullName}
            mergeData={mergeData}
            onClose={handleCloseEmailComposer}
          />
        ) : (
          <>
            <div className="sales-profile-grid">
              <div className="sales-section-card sales-section-lead">
                <div className="sales-section-head">
                  <h2>Lead Information</h2>
                  <p>Primary lead, agency, contact, and ownership details.</p>
                </div>

                <div className="sales-form-fields">
                  <div className="sales-field">
                    <label>Status</label>
                    <select
                      value={salesLead.status || "new"}
                      onChange={(e) => handleChange("status", e.target.value)}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Source</label>
                    <input
                      value={salesLead.source || ""}
                      onChange={(e) => handleChange("source", e.target.value)}
                    />
                  </div>

                  <div className="sales-field">
                    <label>First Name</label>
                    <input
                      value={salesLead.first_name || ""}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                    />
                  </div>

                  <div className="sales-field">
                    <label>Last Name</label>
                    <input
                      value={salesLead.last_name || ""}
                      onChange={(e) => handleChange("last_name", e.target.value)}
                    />
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Agency Name</label>
                    <input
                      value={salesLead.agency_name || ""}
                      onChange={(e) => handleChange("agency_name", e.target.value)}
                    />
                  </div>

                  <div className="sales-field">
                    <label>Phone</label>
                    <input
                      value={salesLead.phone || ""}
                      onChange={(e) => handleChange("phone", e.target.value)}
                    />
                  </div>

                  <div className="sales-field">
                    <label>Email Address</label>
                    <input
                      value={salesLead.email || ""}
                      onChange={(e) => handleChange("email", e.target.value)}
                    />
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Website</label>
                    <input
                      value={salesLead.website || ""}
                      onChange={(e) => handleChange("website", e.target.value)}
                    />
                  </div>

                  <AddressVerificationFields
                    values={salesLead}
                    onChange={handleAddressChange}
                    streetLabel="Business Address"
                    showUnit={false}
                    showCounty={false}
                    fieldMap={{
                      street: "business_address",
                      city: "city",
                      state: "state",
                      zip: "zip_code",
                      county: "county",
                      addressVerified: "address_verified",
                    }}
                  />

                  <div className="sales-field">
                    <label>Owner</label>
                    <select
                      value={salesLead.owner_user_id || ""}
                      onChange={(e) => handleChange("owner_user_id", e.target.value)}
                    >
                      <option value="">Select Owner</option>
                      {owners.map((owner) => (
                        <option key={owner.id} value={owner.id}>
                          {owner.name} {owner.email ? `(${owner.email})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="sales-section-card sales-section-subscription">
                <div className="sales-section-head">
                  <div>
                    <h2>Subscription Information</h2>
                    <p>Subscription interest, plan selection, and billing position.</p>
                  </div>

                  <div className="sales-header-actions">
                    {!isEditingSubscription ? (
                      <button
                        className="sales-secondary-btn"
                        onClick={() => setIsEditingSubscription(true)}
                      >
                        Edit Subscription
                      </button>
                    ) : (
                      <>
                        <button
                          className="sales-secondary-btn"
                          onClick={() => {
                            setIsEditingSubscription(false);
                            loadSalesLead();
                          }}
                        >
                          Cancel Editing
                        </button>
                        <button
                          className="sales-primary-btn"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "Saving..." : "Save Subscription Changes"}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="sales-form-fields">
                  <div className="sales-field">
                    <label>Subscription Status</label>
                    <select
                      value={salesLead.subscription_status || ""}
                      onChange={(e) => handleChange("subscription_status", e.target.value)}
                      disabled={!isEditingSubscription}
                    >
                      <option value="">Select</option>
                      <option value="not_started">Not Started</option>
                      <option value="trial">Trial</option>
                      <option value="active">Active</option>
                      <option value="past_due">Past Due</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Subscription Plan</label>
                    <select
                      value={salesLead.subscription_plan || ""}
                      onChange={(e) => handleChange("subscription_plan", e.target.value)}
                      disabled={!isEditingSubscription}
                    >
                      <option value="">Select Plan</option>
                      {plans.map((plan) => (
                        <option key={plan.id} value={plan.plan_key}>
                          {plan.plan_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sales-field">
                    <label>Billing Status</label>
                    <select
                      value={salesLead.billing_status || ""}
                      onChange={(e) => handleChange("billing_status", e.target.value)}
                      disabled={!isEditingSubscription}
                    >
                      <option value="">Select</option>
                      <option value="pending">Pending</option>
                      <option value="current">Current</option>
                      <option value="past_due">Past Due</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>

                  <div className="sales-field sales-field-full">
                    <label>Current Plan Display</label>
                    <input
                      value={selectedPlan?.plan_name || salesLead.subscription_plan || ""}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            <ActivityLogPanel
              entityType="sales_lead"
              entityId={salesLead.id || id}
              title="Notes & Activity"
              description="Track manual notes and system activity for this sales lead."
            />
          </>
        )}
      </div>
    </>
  );
}

export default SalesLeadProfile;